import { useMemo, useState } from 'react'
import { useVisibility } from '@/hooks/useVisibility'
import type { VisibilityRow } from '@/lib/schemas/visibility'
import {
  filterByCategory,
  searchRows,
  type VisibilityFilter,
} from '@/lib/visibility-filters'
import {
  sellableBadge,
  latestCheckedAt,
  relativeTime,
  summarize,
} from '@/lib/visibility-display'

const TH =
  'px-3 py-2 text-left text-xs font-semibold tracking-wide text-gray-500 uppercase whitespace-nowrap'

const FILTERS: { key: VisibilityFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'divergent', label: 'Divergent only' },
  { key: 'sellable', label: 'Sellable' },
  { key: 'not-sellable', label: 'Not sellable' },
]

export default function Visibility() {
  const { data, isLoading, isError, error, refetch, isFetching } = useVisibility()
  const [filter, setFilter] = useState<VisibilityFilter>('all')
  const [search, setSearch] = useState('')

  const rows = useMemo(() => data?.rows ?? [], [data])
  const summary = useMemo(() => summarize(rows), [rows])
  const lastChecked = useMemo(() => relativeTime(latestCheckedAt(rows)), [rows])
  const visibleRows = useMemo(
    () => searchRows(filterByCategory(rows, filter), search),
    [rows, filter, search],
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-lg font-semibold">Product visibility</h1>
        {isFetching && !isLoading && (
          <span className="text-xs text-gray-400">Refreshing…</span>
        )}
      </div>

      <p className="border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
        Read-only monitor — sellable state is controlled by the Inventory Planner, not this
        app.
      </p>

      {!isLoading && !isError && rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span>
            <span className="font-semibold tabular-nums">{summary.total}</span> variants
          </span>
          <span className="text-gray-300">·</span>
          <span>
            <span className="font-semibold tabular-nums">{summary.sellable}</span> sellable
          </span>
          <span className="text-gray-300">·</span>
          {summary.divergent > 0 ? (
            <span className="inline-block border border-red-300 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-800">
              {summary.divergent} divergent
            </span>
          ) : (
            <span>
              <span className="font-semibold tabular-nums">0</span> divergent
            </span>
          )}
          <span className="text-gray-300">·</span>
          <span className="text-gray-500">KH stock cache last checked {lastChecked}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`border px-3 py-1 text-sm ${
                filter === f.key
                  ? 'border-gray-800 bg-gray-900 text-white'
                  : 'border-gray-300 text-gray-600 hover:text-black'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search product, SKU, or EAN"
          aria-label="Search products"
          className="w-64 border border-gray-300 px-2 py-1 text-sm"
        />
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading product visibility…</p>
      ) : isError ? (
        <div className="space-y-2">
          <p role="alert" className="text-sm text-red-600">
            {error instanceof Error ? error.message : 'Failed to load product visibility.'}
          </p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="border border-gray-800 px-3 py-1 text-sm hover:bg-gray-900 hover:text-white"
          >
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">No products to monitor</p>
      ) : visibleRows.length === 0 ? (
        <p className="text-sm text-gray-500">No products match your filters</p>
      ) : (
        <div className="overflow-x-auto border border-gray-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className={TH}>Product</th>
                <th className={TH}>SKU</th>
                <th className={TH}>EAN</th>
                <th className={TH}>Price</th>
                <th className={TH}>KH stock</th>
                <th className={TH}>Sellable</th>
                <th className={TH}>Expected (why)</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <Row key={row.variant_id} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function Row({ row }: { row: VisibilityRow }) {
  const badge = sellableBadge(row.inventory_policy)
  const rowClass = row.divergent
    ? 'border-b border-gray-100 border-l-4 border-l-red-500 bg-red-50 align-top last:border-b-0'
    : 'border-b border-gray-100 align-top last:border-b-0'

  return (
    <tr className={rowClass}>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1.5">
          <span className="font-medium">{row.product_title}</span>
          {row.product_status !== 'ACTIVE' && (
            <span className="border border-gray-300 px-1 text-[10px] tracking-wide text-gray-600 uppercase">
              {row.product_status === 'DRAFT' ? 'Draft' : 'Archived'}
            </span>
          )}
        </div>
        {row.variant_title && (
          <div className="mt-0.5 text-xs text-gray-400">{row.variant_title}</div>
        )}
      </td>
      <td className="px-3 py-2 whitespace-nowrap tabular-nums text-gray-600">{row.sku}</td>
      <td className="px-3 py-2 whitespace-nowrap tabular-nums text-gray-600">
        {row.ean ?? <span className="text-gray-400">—</span>}
      </td>
      <td className="px-3 py-2 whitespace-nowrap tabular-nums">${row.price}</td>
      <td className="px-3 py-2 whitespace-nowrap">
        {row.in_kh_catalogue === false ? (
          <span className="text-amber-700">not in KH catalogue</span>
        ) : row.kh_stock == null ? (
          <span className="text-gray-400">—</span>
        ) : (
          <span className="flex items-center gap-1.5">
            <span className="tabular-nums">{row.kh_stock}</span>
            {row.end_of_life && (
              <span
                title="End of life"
                className="border border-amber-300 bg-amber-50 px-1 text-[10px] font-medium text-amber-800"
              >
                EOL
              </span>
            )}
          </span>
        )}
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <span className={`inline-block border px-2 py-0.5 text-xs ${badge.className}`}>
          {badge.label}
        </span>
      </td>
      <td className="px-3 py-2">
        <div className="font-medium">
          {row.expected_policy === 'CONTINUE' ? 'Sellable' : 'Not sellable'}
        </div>
        <div className="mt-0.5 max-w-md text-xs text-gray-400" title={row.expected_reason}>
          {row.expected_reason}
        </div>
      </td>
    </tr>
  )
}
